import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Box, Button, Typography, Tabs, Tab, CircularProgress, Alert, Slider, Select, MenuItem, FormControl, InputLabel, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { sheetsService } from '../services/googleSheetsService';
// --- Import MiniVideoPlayer --- 
import { MiniVideoPlayer } from './MiniVideoPlayer';
// --- Import VideoSelectionDialog --- 
import { VideoSelectionDialog } from './VideoSelectionDialog';
// These imports can be removed since we're not using them anymore
// import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
// import { ResizableBox } from 'react-resizable';
// Import styles needed for react-resizable
// import 'react-resizable/css/styles.css';
// This import can be removed
// import * as ReactDOM from 'react-dom';
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore'; // Remove unused icon
import { SelectChangeEvent } from '@mui/material/Select'; // Import correct type for Select change
// Remove toastify imports entirely
// import { toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import { ToastContainer } from 'react-toastify';

// --- Constants ---
const MASTER_VIDEO_LIST_ID = '1aVv8xC1HIB1XQDDwF8wiLtkB9c-D_g4zPnhFhbizkTo';
const MASTER_VIDEO_LIST_RANGE = 'Sheet1!A:E'; // Assuming A=Song, B=Artist, D=S3 URL, E=Seek In
const INTRO_SETTINGS_CELLS = ['B17', 'B18', 'B19', 'B20', 'B21', 'B22', 'B23', 'B24', 'B25', 'B26'];
const OUTRO_SETTINGS_CELLS = ['B33', 'B34', 'B35', 'B36', 'B37', 'B38', 'B39', 'B40', 'B41', 'B42', 'B43', 'B44'];
const ALL_SETTINGS_CELLS = [...INTRO_SETTINGS_CELLS, ...OUTRO_SETTINGS_CELLS];
// Remove unused constant
// const LOCAL_STORAGE_LAYOUT_KEY = 'introOutroLayoutConfig';

// Helper to parse string value to number, removing "px" or "%"
const parsePixels = (value: string | number | undefined): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return parseFloat(value.replace(/px|%/, ''));
  return 0; // Default to 0 if undefined or not parseable
};

// --- Define VideoFormat type at the top level ---
type VideoFormat = 'horizontal' | 'vertical';

// Helper to map slotId to cell addresses
const getCellsForSlot = (slotId: string): { urlCell: string, seekCell: string } | null => {
  if (slotId.startsWith('carousel_video')) {
    const num = parseInt(slotId.replace('carousel_video', ''), 10);
    if (num >= 1 && num <= 4) {
      return { urlCell: `B${17 + (num - 1) * 2}`, seekCell: `B${18 + (num - 1) * 2}` };
    }
  } else if (slotId.startsWith('outro_video')) {
    const num = parseInt(slotId.replace('outro_video', ''), 10);
    if (num >= 1 && num <= 6) {
      return { urlCell: `B${37 + (num - 1) * 2}`, seekCell: `B${38 + (num - 1) * 2}` };
    }
  } else if (slotId === 'video_left') {
    return { urlCell: 'B25', seekCell: 'B26' }; // video_left now has URL in B25 and seek in B26
  }
  return null;
};

interface IntroSettings {
  [key: string]: string;
}

interface OutroSettings {
  [key: string]: string;
}

interface MasterVideoInfo {
  song: string;
  artist: string;
  s3Url: string;
  seekIn: string; // Store as string initially
  position?: number; // Add position info
}

// --- Layout Config Interface ---
interface PlayerLayout {
  top: string;    // Store as percentage string e.g., "10%"
  left: string;   // Store as percentage string e.g., "5%"
  width: string;  // Store as pixel string e.g., "150px"
  height: string; // Store as pixel string e.g., "84px"
}
interface LayoutConfig {
  [slotId: string]: PlayerLayout;
}

// --- Default Layout (Now the only layout) ---
const HORIZONTAL_DEFAULT_LAYOUT: LayoutConfig = {
  // Intro Defaults - Increased height by another 30px
  'carousel_video1': { top: '34.59982110939785%', left: '48.0%', width: '130.8671875px', height: '176.056640625px' },
  'carousel_video2': { top: '34.59982110939785%', left: '60.8%', width: '130.8671875px', height: '176.056640625px' },
  'carousel_video3': { top: '34.59982110939785%', left: '73.6%', width: '130.8671875px', height: '176.056640625px' },
  'carousel_video4': { top: '34.59982110939785%', left: '86.4%', width: '130.8671875px', height: '176.056640625px' },
  'video_left': { top: '3.8410472008534895%', left: '1.1618348449328058%', width: '491.142578125px', height: '580.45703125px' },
  // Outro Defaults
  'outro_video1': { top: '0%', left: '0%', width: '151.732421875px', height: '143.00390625px' },
  'outro_video2': { top: '0.2271223534201954%', left: '59.12560036656623%', width: '95.58984375px', height: '97.19921875px' },
  'outro_video3': { top: '0%', left: '68.23107798165137%', width: '366.97265625px', height: '247.720703125px' },
  'outro_video4': { top: '45.04690384072552%', left: '0.19196181516325403%', width: '316.455078125px', height: '353.65625px' },
  'outro_video5': { top: '44.90592415588075%', left: '29.48601109408872%', width: '133.8984375px', height: '151.47265625px' },
  'outro_video6': { top: '38.09929763843646%', left: '82.25559059633022%', width: '192.87890625px', height: '381.23828125px' },
};

// --- Placeholder Vertical Default Layout (Update with actual values) ---
const VERTICAL_DEFAULT_LAYOUT: LayoutConfig = {
  // Intro Defaults - Placeholder, copy of horizontal for now
  'carousel_video1': { top: '53%', left: '10%', width: '97px', height: '145px' }, // Example, adjust
  'carousel_video2': { top: '53%', left: '30%', width: '97px', height: '145px' }, // Example, adjust
  'carousel_video3': { top: '53%', left: '50%', width: '97px', height: '145px' }, // Example, adjust
  'carousel_video4': { top: '53%', left: '70%', width: '97px', height: '145px' }, // Aligned with other carousel videos
  'video_left': { top: '1%', left: '20.2%', width: '320px', height: '180px' },        // Example, adjust
  // Outro Defaults - Placeholder, copy of horizontal for now (5 videos)
  'outro_video1': { top: '0%', left: '0%', width: '270px', height: '270px' },   // 270px x 270px
  'outro_video3': { top: '15%', left: '72%', width: '150px', height: '106px' },  // Width changed to 150px
  'outro_video4': { top: '37%', left: '0%', width: '149px', height: '127px' }, // Scaled up 5% more and moved up 2%
  'outro_video5': { top: '50%', left: '28%', width: '140px', height: '130px' }, // Back to 50%
  'outro_video6': { top: '50%', left: '53%', width: '315px', height: '540px' }, // Moved down 5% to 50%
};

// Add the setChartDate prop to component props
interface IntroOutroPageProps {
  setChartDate?: React.Dispatch<React.SetStateAction<string>>;
}

export const IntroOutroPage: React.FC<IntroOutroPageProps> = ({ setChartDate }) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  
  // Get renderSheetId from URL params or location state
  const renderSheetIdFromParams = params.renderSheetId;
  const renderSheetIdFromState = location.state?.renderSheetId;
  const renderSheetId = renderSheetIdFromParams || renderSheetIdFromState;
  
  // Add debug logging
  console.log('[IntroOutro] URL params renderSheetId:', renderSheetIdFromParams);
  console.log('[IntroOutro] State renderSheetId:', renderSheetIdFromState);
  console.log('[IntroOutro] Using renderSheetId:', renderSheetId);
  
  // Set chart date from location state immediately if available
  const formattedDateFromState = location.state?.formattedDate;
  useEffect(() => {
    if (formattedDateFromState && setChartDate) {
      console.log(`[IntroOutro] Setting chart date from navigation state: ${formattedDateFromState}`);
      setChartDate(formattedDateFromState);
    }
  }, [formattedDateFromState, setChartDate]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [masterVideoMap, setMasterVideoMap] = useState<Map<string, MasterVideoInfo>>(new Map());
  const [currentWeekVideos, setCurrentWeekVideos] = useState<MasterVideoInfo[]>([]);
  const [cleanupNotification, setCleanupNotification] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  
  // Add missing state variables
  // --- Revert introSettings and outroSettings state to simple objects ---
  const [introSettings, setIntroSettings] = useState<IntroSettings>({});
  const [outroSettings, setOutroSettings] = useState<OutroSettings>({});
  
  const [selectedSlotForSlider, setSelectedSlotForSlider] = useState<string>('video_left');
  const [activeTab, setActiveTab] = useState<number>(0);
  const [sliderValue, setSliderValue] = useState<number>(0);
  const [timeInputValue, setTimeInputValue] = useState<string>('0.0');
  const [isUpdatingSheet, setIsUpdatingSheet] = useState<boolean>(false);
  const [isDraggingSlider, setIsDraggingSlider] = useState<boolean>(false);
  const [currentDateString, setCurrentDateString] = useState<string>('');
  const [renderSheetSeekInColIndex, setRenderSheetSeekInColIndex] = useState<number>(-1);

  // Create refs to break the circular dependency
  const loadDataRef = useRef<(() => Promise<void>) | null>(null);
  const playerAreaRef = useRef<HTMLDivElement>(null); // Ref for the player area container
  
  // Add this ref to track cleanup runs
  const cleanupRunRef = useRef<{[key: string]: boolean}>({});

  // Add a way to extract the current date from the sheet
  const [currentDate, setCurrentDate] = useState<string>('');

  // Use a ref to track dates that have had videos cleaned up
  const dateCleanupRunRef = useRef<{[key: string]: boolean}>({});

  // --- Add state for current video format ---
  const [currentFormat, setCurrentFormat] = useState<VideoFormat>('horizontal');

  // --- State for dynamic layout configuration ---
  const [dynamicLayoutConfig, setDynamicLayoutConfig] = useState<LayoutConfig>(
    currentFormat === 'horizontal' ? HORIZONTAL_DEFAULT_LAYOUT : VERTICAL_DEFAULT_LAYOUT
  );

  // --- Handler for format change ---
  const handleFormatChange = (
    event: React.MouseEvent<HTMLElement>,
    newFormat: VideoFormat | null, // newFormat can be null if no button is pressed
  ) => {
    if (newFormat) { // Check if newFormat is not null
      setCurrentFormat(newFormat);
      // TODO: Potentially trigger data reload or settings update here
      // For now, it will just change the displayed layout and background
      console.log(`[IntroOutro] Format changed to: ${newFormat}`);
    }
  };

  useEffect(() => {
    // Initialize or reset dynamicLayoutConfig when currentFormat or activeTab changes
    const newBaseLayout = currentFormat === 'horizontal' ? HORIZONTAL_DEFAULT_LAYOUT : VERTICAL_DEFAULT_LAYOUT;
    setDynamicLayoutConfig(newBaseLayout);
    console.log('[IntroOutro] dynamicLayoutConfig reset due to format/tab change.');
  }, [currentFormat, activeTab]);

  // Effect to convert percentage positions to pixels for vertical outro draggable items
  useEffect(() => {
    if (currentFormat === 'vertical' && activeTab === 1 && playerAreaRef.current) {
      const parentWidth = playerAreaRef.current.offsetWidth;
      const parentHeight = playerAreaRef.current.offsetHeight;
      
      setDynamicLayoutConfig(prevConfig => {
        const newConfig = { ...prevConfig };
        let updated = false;

        Object.keys(prevConfig).forEach(slotId => {
          if (slotId.startsWith('outro_video')) { // Only for outro videos in vertical mode
            const slotLayout = { ...prevConfig[slotId] };
            const baseLayout = VERTICAL_DEFAULT_LAYOUT[slotId] || {}; // Fallback to empty if not in default

            // Convert top from percentage to pixels if needed
            if (typeof slotLayout.top === 'string' && slotLayout.top.includes('%')) {
              slotLayout.top = `${(parsePixels(baseLayout.top || '0%') / 100) * parentHeight}px`;
              updated = true;
            }
            // Convert left from percentage to pixels if needed
            if (typeof slotLayout.left === 'string' && slotLayout.left.includes('%')) {
              slotLayout.left = `${(parsePixels(baseLayout.left || '0%') / 100) * parentWidth}px`;
              updated = true;
            }
            // Ensure width and height are pixel strings
            if (typeof slotLayout.width === 'string' && !slotLayout.width.includes('px')) {
                 slotLayout.width = `${parsePixels(slotLayout.width)}px`;
                 updated = true;
            } else if (typeof slotLayout.width !== 'string') {
                slotLayout.width = `${parsePixels(baseLayout.width || '90px')}px`; // Default if not set
                updated = true;
            }

            if (typeof slotLayout.height === 'string' && !slotLayout.height.includes('px')) {
                slotLayout.height = `${parsePixels(slotLayout.height)}px`;
                updated = true;
            } else if (typeof slotLayout.height !== 'string') {
                slotLayout.height = `${parsePixels(baseLayout.height || '160px')}px`; // Default if not set
                updated = true;
            }
            newConfig[slotId] = slotLayout;
          }
        });
        if (updated) {
            console.log('[IntroOutro] Converted % to px for dynamicLayoutConfig (Vertical Outro)', newConfig);
            return newConfig;
        }
        return prevConfig;
      });
    }
  }, [currentFormat, activeTab, playerAreaRef, loading]); // Added loading to re-run when playerAreaRef becomes available

  // Add a function to save current week videos to local storage
  const saveCurrentWeekVideosToLocalStorage = useCallback((videos: MasterVideoInfo[]) => {
    if (videos.length > 0) {
      console.log(`[IntroOutro] Saving ${videos.length} current week videos to local storage`);
      localStorage.setItem(`billboard_current_week_videos_${renderSheetId || ''}`, JSON.stringify(videos));
    }
  }, [renderSheetId]);

  // Add a function to load current week videos from local storage
  const loadCurrentWeekVideosFromLocalStorage = useCallback((): MasterVideoInfo[] => {
    try {
      const storedVideos = localStorage.getItem(`billboard_current_week_videos_${renderSheetId || ''}`);
      if (storedVideos) {
        const videos = JSON.parse(storedVideos) as MasterVideoInfo[];
        console.log(`[IntroOutro] Loaded ${videos.length} current week videos from local storage`);
        return videos;
      }
    } catch (error) {
      console.error('[IntroOutro] Error loading videos from local storage:', error);
    }
    return [];
  }, [renderSheetId]);

  // Rename and modify the sync function to handle all columns
  const syncWeeklyDataToRenderSheet = useCallback(async (weeklySheetId: string) => {
    if (!weeklySheetId || !renderSheetId) return;
    
    console.log('[IntroOutro] Syncing weekly sheet data to render sheet...');
    
    try {
      // Get data from weekly sheet - specifically rows 2-13 and columns A-Q
      const weeklyData = await sheetsService.getData(weeklySheetId, 'Sheet1!A2:Q13');
      if (!weeklyData?.values) {
        console.warn('[IntroOutro] No data found in weekly sheet');
        return;
      }

      // Ensure all cells exist in each row (including empty cells)
      // This makes sure empty cells in the weekly sheet are preserved as empty in the render sheet
      const processedValues = weeklyData.values.map(row => {
        // Find the maximum column index (Q = 16, zero-based index)
        const maxColIndex = 16; 
        // Create an array with length maxColIndex+1, filling missing values with empty strings
        const processedRow = Array(maxColIndex + 1).fill('');
        
        // Fill in values that exist
        row.forEach((value, index) => {
          if (index <= maxColIndex) {
            processedRow[index] = value === null || value === undefined ? '' : value;
          }
        });
        
        return processedRow;
      });

      // Get URLs from M12 and M13
      const urlData = await sheetsService.getData(weeklySheetId, 'Sheet1!M12:M13');
      const introUrl = urlData?.values?.[0]?.[0] || '';
      const outroUrl = urlData?.values?.[1]?.[0] || '';

      // Prepare all updates
      const updates = [
        {
          range: 'Sheet1!A2:Q13',
          values: processedValues
        },
        {
          range: 'Sheet1!B15',
          values: [[introUrl]]
        },
        {
          range: 'Sheet1!B35',
          values: [[outroUrl]]
        }
      ];

      // Apply all updates in one batch
      await sheetsService.batchUpdateValues(renderSheetId, updates);
      console.log('[IntroOutro] Successfully synced weekly data and URLs to render sheet');

    } catch (err) {
      console.error('[IntroOutro] Error syncing weekly data:', err);
    }
  }, [renderSheetId]);

  // --- Data Loading functions ---
  const cleanupOutdatedVideos = useCallback(async (weekVideos: MasterVideoInfo[]) => {
    if (!renderSheetId) return; // Only return if no renderSheetId, but continue if weekVideos is empty
    
    console.log('[IntroOutro] Checking for outdated videos to clean up...');
    console.log(`[IntroOutro] Current sheet ID: ${renderSheetId}`);
    console.log(`[IntroOutro] Current date: ${currentDateString}`);
    console.log(`[IntroOutro] Current week videos count: ${weekVideos.length}`);
    
    // Get clean list of current week video filenames for comparison
    const currentVideoFilenames = weekVideos.map(v => {
      // Extract just the filename without any query parameters and decode URL encoding
      const filename = v.s3Url.split('/').pop()?.split('?')[0] || '';
      const decodedFilename = decodeURIComponent(filename).toLowerCase();
      console.log(`[IntroOutro] Current week video: ${decodedFilename} (${v.song} by ${v.artist})`);
      return decodedFilename;
    });
    
    console.log(`[IntroOutro] Current week has ${currentVideoFilenames.length} videos`);
    
    // Define intro and outro slots
    const introSlots = ['carousel_video1', 'carousel_video2', 'carousel_video3', 'carousel_video4', 'video_left'];
    const outroSlots = ['outro_video1', 'outro_video2', 'outro_video3', 'outro_video4', 'outro_video5', 'outro_video6'];
    const allSlots = [...introSlots, ...outroSlots];
    
    // Array to collect batch updates
    const updates: { range: string; values: any[][] }[] = [];
    const removedVideos: {slotId: string; song?: string; artist?: string; url: string}[] = [];
    
    // Loop through all possible intro/outro slots and check if they contain current week videos
    for (const slotId of allSlots) {
      // Get the URL for this slot
      let slotUrl = '';
      if (introSlots.includes(slotId)) {
        slotUrl = (introSettings as any)[`${slotId}_url`] || '';
      } else if (outroSlots.includes(slotId)) {
        slotUrl = (outroSettings as any)[`${slotId}_url`] || '';
      }
      
      if (!slotUrl) {
        console.log(`[IntroOutro] Skipping empty slot: ${slotId}`);
        continue; // Skip empty slots
      }
      
      // Extract the clean filename without any query parameters and decode URL encoding
      const filename = slotUrl.split('/').pop()?.split('?')[0] || '';
      const slotFilename = decodeURIComponent(filename).toLowerCase();
      console.log(`[IntroOutro] Checking ${slotId} with filename: ${slotFilename}`);
      
      // If we have no videos in current week or the video isn't in the current week's list, clean it up
      let isInCurrentWeek = false;
      
      // Only check if video is in current week if we have videos to check against
      if (currentVideoFilenames.length > 0) {
        isInCurrentWeek = currentVideoFilenames.some(currentFilename => {
          // Exact match
          if (currentFilename === slotFilename) {
            console.log(`[IntroOutro] Exact filename match: ${slotFilename} === ${currentFilename}`);
            return true;
          }
          
          // Substantial partial matches (for files with different extensions or slight name variations)
          if (slotFilename.length > 10 && currentFilename.includes(slotFilename)) {
            console.log(`[IntroOutro] Partial match: ${currentFilename} contains ${slotFilename}`);
            return true;
          }
          
          if (currentFilename.length > 10 && slotFilename.includes(currentFilename)) {
            console.log(`[IntroOutro] Partial match: ${slotFilename} contains ${currentFilename}`);
            return true;
          }
          
          // Remove extensions for another comparison attempt
          const slotFileBase = slotFilename.replace(/\.(mp4|mov|webm|avi)$/i, '');
          const currentFileBase = currentFilename.replace(/\.(mp4|mov|webm|avi)$/i, '');
          
          if (slotFileBase === currentFileBase) {
            console.log(`[IntroOutro] Base filename match (ignoring extension): ${slotFileBase} === ${currentFileBase}`);
            return true;
          }
          
          return false;
        });
      }
      
      console.log(`[IntroOutro] Is ${slotId} in current week's top 10? ${isInCurrentWeek}`);
      
      // If the video is not in the current week (or there are no current week videos), clean it up
      if (!isInCurrentWeek) {
        console.log(`[IntroOutro] Cleaning up ${slotId} as it's not in current week's top 10`);
        
        // Get cell addresses for this slot
        const cells = getCellsForSlot(slotId);
        if (cells) {
          // Add to batch updates
          updates.push({ range: `Sheet1!${cells.urlCell}`, values: [['']] });
          updates.push({ range: `Sheet1!${cells.seekCell}`, values: [['0']] });
          
          // Try to find song and artist info for this URL
          let songInfo = null;
          
          // Helper function to extract and normalize filename
          const extractFilename = (fileUrl: string): string => {
            const filename = fileUrl.split('/').pop() || '';
            return filename.split('?')[0].toLowerCase();
          };
          
          // Look in master video map
          songInfo = Array.from(masterVideoMap.values()).find(info => 
            info.s3Url.trim() === slotUrl.trim() || 
            extractFilename(info.s3Url) === extractFilename(slotUrl)
          );
          
          // If not found, check current week videos (unlikely but just in case)
          if (!songInfo) {
            songInfo = currentWeekVideos.find(info => 
              info.s3Url.trim() === slotUrl.trim() || 
              extractFilename(info.s3Url) === extractFilename(slotUrl)
            );
          }
          
          // Add to removed videos
          removedVideos.push({
            slotId,
            song: songInfo?.song,
            artist: songInfo?.artist, 
            url: slotUrl
          });
        }
      }
    }
    
    // If we have updates, send batch update to Google Sheets
    if (updates.length > 0 && renderSheetId) {
      console.log(`[IntroOutro] Cleaning up ${removedVideos.length} outdated videos`);
      
      try {
        await sheetsService.batchUpdateValues(renderSheetId, updates);
        console.log('[IntroOutro] Successfully cleaned up outdated videos');
        
        // Update local state
        for (const video of removedVideos) {
          if (introSlots.includes(video.slotId)) {
            setIntroSettings(prev => ({
              ...prev,
              [`${video.slotId}_url`]: '',
              [`${video.slotId}_seek`]: '0'
            }));
          } else if (outroSlots.includes(video.slotId)) {
            setOutroSettings(prev => ({
              ...prev,
              [`${video.slotId}_url`]: '',
              [`${video.slotId}_seek`]: '0'
            }));
          }
        }
        
        // Show cleanup notification
        if (removedVideos.length > 0) {
          const removedMessages = removedVideos.map(video => {
            // Try to get song and artist from songInfo
            if (video.song && video.artist) {
              return `Removed "${video.song}" by ${video.artist} because it's not in this week's chart.`;
            } else {
              // If song and artist info not available, try to extract from filename
              const filename = video.url.split('/').pop() || '';
              const decodedFilename = decodeURIComponent(filename);
              
              // Attempt to parse song and artist from filename
              let song = "";
              let artist = "";
              
              // Common filename patterns: "Artist - Song.mp4" or "Song - Artist.mp4"
              if (decodedFilename.includes(' - ')) {
                const parts = decodedFilename.split(' - ');
                if (parts.length >= 2) {
                  // Assume first part is artist, second part is song (removing .mp4 extension)
                  artist = parts[0].trim();
                  song = parts[1].replace(/\.(mp4|mov|avi|webm)$/i, '').trim();
                  
                  // Check if we need to swap (song tends to have more words than artist name)
                  // This is a heuristic that might not always be correct
                  if (artist.split(' ').length > song.split(' ').length && !artist.includes('feat.')) {
                    // Swap them - artist name is usually shorter than song title
                    [artist, song] = [song, artist];
                  }
                }
              } else {
                // If no separator, just use the filename as the song
                song = decodedFilename.replace(/\.(mp4|mov|avi|webm)$/i, '');
                artist = "Unknown Artist";
              }
              
              return `Removed "${song}" by ${artist} because it's not in this week's chart.`;
            }
          });
          
          setCleanupNotification(removedMessages.join('\n'));
          // Clear notification after 15 seconds
          setTimeout(() => setCleanupNotification(null), 15000);
        }
      } catch (err) {
        console.error('[IntroOutro] Error cleaning up videos:', err);
        setError(err instanceof Error ? err.message : 'Failed to clean up outdated videos.');
      }
    } else {
      console.log('[IntroOutro] No videos to clean up');
    }
  }, [renderSheetId, introSettings, outroSettings, setIntroSettings, setOutroSettings, setCleanupNotification, setError, masterVideoMap, currentWeekVideos, currentDateString]);

  // Add a function to extract date from sheet if possible
  const extractDateFromSheet = useCallback(async () => {
    if (!renderSheetId) return;
    
    try {
      const dateData = await sheetsService.getData(renderSheetId, "Sheet1!B28");
      if (dateData?.values && dateData.values.length > 0 && dateData.values[0].length > 0) {
        const dateString = String(dateData.values[0][0] || '');
        console.log(`[IntroOutro] Extracted date from sheet: ${dateString}`);
        setCurrentDateString(dateString);
        
        // Reset cleanup tracking whenever a new date is loaded
        if (dateString && !dateCleanupRunRef.current[dateString]) {
          console.log(`[IntroOutro] New date detected: ${dateString}, resetting cleanup tracking`);
          dateCleanupRunRef.current[dateString] = true;
          cleanupRunRef.current = {}; // Reset video cleanup status for new date
        }
      }
    } catch (err) {
      console.error('[IntroOutro] Error extracting date from sheet:', err);
    }
  }, [renderSheetId]);

  // Update loadData to call extractDateFromSheet
  const loadData = useCallback(async () => {
    if (!renderSheetId) {
      setError('Render Sheet ID not provided.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Extract date from sheet
      await extractDateFromSheet();
      
      // Rest of existing loadData implementation...

      // Get the sheet name from location state
      const sheetName = location.state?.sheetName || '';
      
      // Parse date from sheet name if it matches the pattern (e.g., "5-3-25-render")
      if (sheetName.includes('-render')) {
        const dateMatch = sheetName.match(/(\d+)-(\d+)-(\d+)-render/);
        if (dateMatch) {
          const [, month, day, year] = dateMatch;
          // Convert month number to month name
          const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                            'July', 'August', 'September', 'October', 'November', 'December'];
          const monthIndex = parseInt(month) - 1;
          const fullYear = parseInt(year) < 100 ? 2000 + parseInt(year) : parseInt(year);
          
          // Format the date (e.g., "May 3, 2025")
          const formattedDate = `${monthNames[monthIndex]} ${parseInt(day)}, ${fullYear}`;
          
          // Update cell B28 with the formatted date
          await sheetsService.batchUpdateValues(renderSheetId, [{
            range: 'Sheet1!B28',
            values: [[formattedDate]]
          }]);
          
          // Update App component state if setChartDate prop is available
          if (setChartDate) {
            setChartDate(formattedDate);
          }
          
          console.log(`[IntroOutro] Updated sheet date to: ${formattedDate}`);
        }
      }

      // First try to load videos from local storage for immediate use
      const cachedVideos = loadCurrentWeekVideosFromLocalStorage();
      if (cachedVideos.length > 0) {
        setCurrentWeekVideos(cachedVideos);
        
        // Debug if Ordinary is in cached videos
        const ordinaryInCached = cachedVideos.find(v => v.song === "Ordinary" || v.s3Url.toLowerCase().includes("ordinary"));
        if (ordinaryInCached) {
          console.log(`[IntroOutro] Found Ordinary in cached videos: seekIn = ${ordinaryInCached.seekIn}, URL = ${ordinaryInCached.s3Url}`);
        } else {
          console.log(`[IntroOutro] Ordinary NOT found in cached videos`);
        }
      }

      // 1. Fetch Master Video List
      console.log('[IntroOutro] Fetching Master Video List...');
      const masterData = await sheetsService.getData(MASTER_VIDEO_LIST_ID, MASTER_VIDEO_LIST_RANGE);
      if (!masterData?.values) {
        console.error('[IntroOutro] Failed to load master video list or no data available.');
        return;
      }
      
      // Find column indices
      const headers = masterData.values[0].map((cell: any) => String(cell || '').toLowerCase().trim());
      // --- ADD console log for raw headers ---
      console.log('[IntroOutro] Processed Headers for Index Search:', JSON.stringify(headers));
      // --- END console log ---
      
      const songIndex = headers.findIndex(h => h.includes('song') || h.includes('title'));
      const artistIndex = headers.findIndex(h => h.includes('artist'));
      // --- Prioritize "s3 link" check ---
      const s3UrlIndex = headers.findIndex(h => h === 's3 link' || h.includes('s3 url') || (h.includes('s3') && h.includes('link')) );
      // --- End S3 URL check ---
      const seekInIndex = headers.findIndex(h => h.toLowerCase().includes('seek in'));
      
      // --- Add debugging to see what we found ---
      console.log('[IntroOutro] Master list column indices:');
      console.log(`  songIndex: ${songIndex}, header: ${songIndex >= 0 ? headers[songIndex] : 'not found'}`);
      console.log(`  artistIndex: ${artistIndex}, header: ${artistIndex >= 0 ? headers[artistIndex] : 'not found'}`);
      console.log(`  s3UrlIndex: ${s3UrlIndex}, header: ${s3UrlIndex >= 0 ? headers[s3UrlIndex] : 'not found'}`);
      console.log(`  seekInIndex: ${seekInIndex}, header: ${seekInIndex >= 0 ? headers[seekInIndex] : 'not found'}`);
      
      // --- Adjust indices if needed (fallback to standard columns) ---
      // If we couldn't find the columns by their names, use standard indices
      if (songIndex === -1) console.log('[IntroOutro] Song column not found, using column A (index 0)');
      if (artistIndex === -1) console.log('[IntroOutro] Artist column not found, using column B (index 1)');
      if (s3UrlIndex === -1) console.log('[IntroOutro] S3 URL column not found, using column C (index 2)'); // Correct fallback to C
      if (seekInIndex === -1) console.log('[IntroOutro] Seek In column not found, using column I (index 8)'); // Correct fallback to I
      
      // --- End Adjust indices ---

      if (s3UrlIndex === -1 || seekInIndex === -1) {
        console.error('[IntroOutro] Could not find S3 URL or Seek In columns in Master List.');
        // Potentially set error state here
      } else {
        console.log(`[IntroOutro] Processing data from Master Video List, ${masterData.values.length - 1} videos...`);
        
        const newMasterMap = new Map<string, MasterVideoInfo>();
        
        // Start from row 1 (index 1) to skip headers
        masterData.values.slice(1).forEach((row: any[]) => {
          // Skip if the row doesn't have the S3 URL column
          if (!row[s3UrlIndex]) return;
          
          const s3Url = String(row[s3UrlIndex] ?? '').trim();
          const seekIn = String(row[seekInIndex] ?? '0').trim(); // Actually use the seekInIndex to get the seek value
          const song = String(row[songIndex >= 0 ? songIndex : 0] ?? '').trim();
          const artist = String(row[artistIndex >= 0 ? artistIndex : 1] ?? '').trim();
          
          if (s3Url) {
            newMasterMap.set(s3Url, { s3Url, seekIn, song, artist });
            
            // Special debug for Ordinary and Beautiful Things
            if (song === "Ordinary" || s3Url.toLowerCase().includes("ordinary") ||
                song === "Beautiful Things" || s3Url.toLowerCase().includes("beautiful things")) {
              console.log(`[IntroOutro] Added ${song} to master map: seekIn = ${seekIn}, URL = ${s3Url}`);
            }
          }
        });
        
        setMasterVideoMap(newMasterMap);
        console.log(`[IntroOutro] Master Video List loaded with ${newMasterMap.size} entries.`);

        // Debug check for Ordinary in master map
        console.log('[IntroOutro] Checking for Ordinary in master map:');
        const ordinaryEntries = Array.from(newMasterMap.values()).filter(
          (v: MasterVideoInfo) => v.song === "Ordinary" || v.s3Url.toLowerCase().includes("ordinary")
        );
        if (ordinaryEntries.length > 0) {
          ordinaryEntries.forEach((entry: MasterVideoInfo) => {
            console.log(`[IntroOutro] Master map has Ordinary: seekIn = ${entry.seekIn}, URL = ${entry.s3Url}`);
          });
        } else {
          console.log('[IntroOutro] Ordinary NOT found in master map');
        }
      }

      // 2. Fetch Settings from Render Sheet
      console.log(`[IntroOutro] Fetching settings from Render Sheet: ${renderSheetId}`);
      
      // IMPROVED DEBUGGING: Log the cells we're attempting to fetch
      console.log('[IntroOutro] Requesting the following cells:', ALL_SETTINGS_CELLS);
      
      // Use a direct range fetch instead of batch to simplify debugging
      // This gets a range that encompasses all the cells we need
      console.log('[IntroOutro] Using direct range fetch for more reliable results');
      // ---> START: Use direct range fetch for B16:B48 (Extended Range)
      console.log('[IntroOutro loadData] Fetching settings via direct range: Sheet1!B16:B48');
      const rawSettingsData = await sheetsService.getData(renderSheetId, 'Sheet1!B16:B48'); // Use extended range
      // Log raw response from direct fetch
      console.log('[IntroOutro loadData] Raw settings data received (B16:B48):', JSON.stringify(rawSettingsData, null, 2));
      // ---> END: Use direct range fetch

      const newIntroSettings: IntroSettings = {};
      const newOutroSettings: OutroSettings = {};

      // Helper function to extract cell values from the rawSettingsData.values array
      // Note: row 0 in the array corresponds to B16, row 1 to B17, etc.
      const getCellValueFromRaw = (cellRef: string): string | undefined => {
        const rowNumMatch = cellRef.match(/B(\d+)/);
        if (!rowNumMatch) return undefined; // Invalid cell ref format

        const targetRowNum = parseInt(rowNumMatch[1], 10);
        const dataArrayIndex = targetRowNum - 16; // Calculate index relative to B16

        if (dataArrayIndex < 0 || !rawSettingsData?.values || dataArrayIndex >= rawSettingsData.values.length) {
          // Log if index is out of bounds
          console.log(`[IntroOutro loadData] Raw data index out of bounds for cell ${cellRef} (index ${dataArrayIndex})`);
          return undefined;
        }
        const value = rawSettingsData.values[dataArrayIndex]?.[0]; // Value should be in the first column (index 0) of the fetched range
        // Log the value found at the specific index
        console.log(`[IntroOutro loadData] Parsed Raw: ${cellRef} (index ${dataArrayIndex}) = ${value || 'undefined'}`);
        return value ? String(value) : undefined; // Return as string or undefined
      };


      // Populate Intro Settings using the helper
      newIntroSettings.carousel_video1_url = getCellValueFromRaw('B17') || '';
      newIntroSettings.carousel_video1_seek = getCellValueFromRaw('B18') || '0';
      newIntroSettings.carousel_video2_url = getCellValueFromRaw('B19') || '';
      newIntroSettings.carousel_video2_seek = getCellValueFromRaw('B20') || '0';
      newIntroSettings.carousel_video3_url = getCellValueFromRaw('B21') || '';
      newIntroSettings.carousel_video3_seek = getCellValueFromRaw('B22') || '0';
      newIntroSettings.carousel_video4_url = getCellValueFromRaw('B23') || '';
      newIntroSettings.carousel_video4_seek = getCellValueFromRaw('B24') || '0';
      newIntroSettings.video_left_url = getCellValueFromRaw('B25') || '';
      newIntroSettings.video_left_seek = getCellValueFromRaw('B26') || '0';

      // Get the chart date from cell B28
      const dateFromSheet = getCellValueFromRaw('B28') || '';
      if (dateFromSheet) {
        console.log(`[IntroOutro] Found chart date in B28: ${dateFromSheet}`);
        
        // Update App component state if setChartDate prop is available
        if (setChartDate) {
          setChartDate(dateFromSheet);
        }
      }

      // Populate Outro Settings using the helper
      newOutroSettings.outro_video1_url = getCellValueFromRaw('B37') || '';
      newOutroSettings.outro_video1_seek = getCellValueFromRaw('B38') || '0';
      newOutroSettings.outro_video2_url = getCellValueFromRaw('B39') || '';
      newOutroSettings.outro_video2_seek = getCellValueFromRaw('B40') || '0';
      newOutroSettings.outro_video3_url = getCellValueFromRaw('B41') || '';
      newOutroSettings.outro_video3_seek = getCellValueFromRaw('B42') || '0';
      newOutroSettings.outro_video4_url = getCellValueFromRaw('B43') || '';
      newOutroSettings.outro_video4_seek = getCellValueFromRaw('B44') || '0';
      newOutroSettings.outro_video5_url = getCellValueFromRaw('B45') || '';
      newOutroSettings.outro_video5_seek = getCellValueFromRaw('B46') || '0';
      newOutroSettings.outro_video6_url = getCellValueFromRaw('B47') || '';
      newOutroSettings.outro_video6_seek = getCellValueFromRaw('B48') || '0';

      // Log the loaded settings
      // ---> Log final objects before setState
      console.log('[IntroOutro loadData] Final introData before setState:', JSON.stringify(newIntroSettings, null, 2));
      console.log('[IntroOutro loadData] Final outroData before setState:', JSON.stringify(newOutroSettings, null, 2));
      // ---> END: Log final objects

      // Prevent setting empty objects if our data retrieval failed
      if (Object.keys(newIntroSettings).length > 0 || Object.values(newIntroSettings).some(v => v !== '' && v !== '0')) {
        // Simplified: set directly, as settings are shared
        setIntroSettings(newIntroSettings);
      } else {
        console.error('[IntroOutro] Failed to load intro settings - object is empty');
      }
      
      if (Object.keys(newOutroSettings).length > 0 || Object.values(newOutroSettings).some(v => v !== '' && v !== '0')) {
        // Simplified: set directly, as settings are shared
        setOutroSettings(newOutroSettings);
      } else {
        console.error('[IntroOutro] Failed to load outro settings - object is empty');
      }

      // 3. Sync weekly data to render sheet
      const weeklySheetId = localStorage.getItem('currentSpreadsheetId');
      if (weeklySheetId) {
        await syncWeeklyDataToRenderSheet(weeklySheetId);
      }

      // 4. Fetch current week's videos and run cleanup
      try {
        console.log(`[IntroOutro] Fetching current week's videos from Render Sheet: ${renderSheetId}`);
        const videoData = await sheetsService.getData(renderSheetId, "Sheet1!A1:Z11"); // Get top 10 rows (includes header)
        
        if (videoData?.values && videoData.values.length > 1) {
          // Find the column indices for song, artist, S3 URL, and SEEK IN
          const headers = videoData.values[0].map((h: any) => String(h).toLowerCase().trim());
          const songColIndex = headers.findIndex(h => h.includes('song') || h.includes('title'));
          const artistColIndex = headers.findIndex(h => h.includes('artist'));
          const s3UrlColIndex = headers.findIndex(h => h.includes('s3'));
          const positionColIndex = headers.findIndex(h => h.includes('position') || h === 'pos');
          // ---> START: Find Seek In column index from Render Sheet headers
          const seekInColIndex = headers.findIndex(h => h.toLowerCase().includes('seek in'));
          console.log(`[IntroOutro] Render sheet column indices: song=${songColIndex}, artist=${artistColIndex}, s3=${s3UrlColIndex}, pos=${positionColIndex}, seekIn=${seekInColIndex}`);
          // ---> END: Find Seek In column index
          // ---> START: Set state for column index
          setRenderSheetSeekInColIndex(seekInColIndex);
          // ---> END: Set state

          if (songColIndex !== -1 && artistColIndex !== -1 && s3UrlColIndex !== -1) {
            // Process video data
            const weekVideos: MasterVideoInfo[] = [];
            
            videoData.values.slice(1).forEach((row: any[], index: number) => {
              const song = String(row[songColIndex] ?? '').trim();
              const artist = String(row[artistColIndex] ?? '').trim();
              // Generate correct S3 URL based on song and artist instead of trusting sheet data
              const s3Url = song && artist ? 
                `https://c1-scott-scratchdisk.s3.us-west-2.amazonaws.com/Billboard/Videos/${encodeURIComponent(artist)} - ${encodeURIComponent(song)}.mp4` :
                String(row[s3UrlColIndex] ?? '').trim(); // Fallback to sheet URL if song/artist missing
              const position = positionColIndex !== -1 ? 
                parseInt(String(row[positionColIndex] ?? '').trim(), 10) || (index + 1) : 
                (index + 1);
              
              console.log(`[IntroOutro] Position ${position}: Generated correct S3 URL for ${artist} - ${song}: ${s3Url}`);
              
              if (s3Url) {
                const trimmedUrl = s3Url.trim();
                let seekIn = '0'; // Default seek time

                // ---> START: Prioritize Seek In from Render Sheet column
                if (seekInColIndex !== -1 && row[seekInColIndex]) {
                  const renderSheetSeekIn = String(row[seekInColIndex]).trim();
                  if (renderSheetSeekIn && renderSheetSeekIn !== '0') {
                    seekIn = renderSheetSeekIn;
                    console.log(`[IntroOutro] Using seek time from Render Sheet for ${song}: ${seekIn}`);
                  }
                }
                // ---> END: Prioritize Seek In from Render Sheet column

                // ---> START: Fallback to Master List if Render Sheet value was missing or '0'
                if (seekIn === '0') { 
                  console.log(`[IntroOutro] Seek time not found or is '0' in Render Sheet for ${song}. Checking Master List...`);
                  const masterInfo = masterVideoMap.get(trimmedUrl);
                  if (masterInfo && masterInfo.seekIn && masterInfo.seekIn !== '0') {
                    seekIn = masterInfo.seekIn;
                    console.log(`[IntroOutro] Found Master List seek time match for ${song}: ${seekIn}`);
                  } else {
                    // Try filename matching in master map as a further fallback
                    const extractFilename = (url: string): string => url.split('/').pop()?.split('?')[0]?.toLowerCase() || '';
                    const urlFilename = extractFilename(trimmedUrl);
                    const matchingMasterEntry = Array.from(masterVideoMap.values()).find((info: MasterVideoInfo) => extractFilename(info.s3Url) === urlFilename);
                    
                    if (matchingMasterEntry && matchingMasterEntry.seekIn && matchingMasterEntry.seekIn !== '0') {
                      seekIn = matchingMasterEntry.seekIn;
                      console.log(`[IntroOutro] Found Master List filename seek time match for ${song}: ${seekIn}`);
                    } else {
                      console.log(`[IntroOutro] No valid seek time found for ${song} in Render Sheet or Master List, defaulting to 0`);
                      // seekIn remains '0'
                    }
                  }
                }
                // ---> END: Fallback to Master List
                
                weekVideos.push({ song, artist, s3Url, seekIn, position });
              }
            });
            
            setCurrentWeekVideos(weekVideos);
            // Save to local storage for future use
            saveCurrentWeekVideosToLocalStorage(weekVideos);
            
            console.log(`[IntroOutro] Current week videos loaded: ${weekVideos.length}`);
            console.log(`[IntroOutro] Current week video URLs with seek times:`, 
              weekVideos.map(v => `${v.song} (${v.s3Url}): ${v.seekIn}`));
          } else {
            console.error('[IntroOutro] Could not find required columns in current week sheet');
          }
        } else {
          console.warn('[IntroOutro] No video data found in current week sheet');
        }
      } catch (err) {
        console.error('[IntroOutro] Error loading current week videos:', err);
        // Don't set error state here - we'll continue even if this fails
      }

      // Set initial selected slot after data loads
      setSelectedSlotForSlider('video_left'); // Default to video_left initially

    } catch (err) {
      console.error('[IntroOutro] Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load page data.');
    } finally {
      setLoading(false);
    }
  }, [renderSheetId, saveCurrentWeekVideosToLocalStorage, loadCurrentWeekVideosFromLocalStorage, syncWeeklyDataToRenderSheet, location.state?.sheetName, masterVideoMap, setError, setLoading, setChartDate]);

  // Store the loadData function in the ref to break the circular dependency
  useEffect(() => {
    loadDataRef.current = loadData;
  }, [loadData]);

  useEffect(() => {
    if (loadDataRef.current) {
      loadDataRef.current();
    }
  }, []);

  // --- Effect to update slider value when selected slot or settings change ---
  useEffect(() => {
    if (!selectedSlotForSlider) return;

    let currentSeekString: string | undefined;
    if (selectedSlotForSlider.startsWith('carousel') || selectedSlotForSlider === 'video_left') {
      currentSeekString = (introSettings as any)[`${selectedSlotForSlider}_seek`];
    } else if (selectedSlotForSlider.startsWith('outro')) {
      currentSeekString = (outroSettings as any)[`${selectedSlotForSlider}_seek`];
    }

    const currentSeekNumber = parseFloat(currentSeekString || '0');
    setSliderValue(isNaN(currentSeekNumber) ? 0 : currentSeekNumber);
    setTimeInputValue((isNaN(currentSeekNumber) ? 0 : currentSeekNumber).toFixed(1));

  }, [selectedSlotForSlider, introSettings, outroSettings]);

  // Add this useEffect to update the text input value when slider value changes
  useEffect(() => {
    setTimeInputValue(sliderValue.toFixed(1));
  }, [sliderValue]);

  // --- Event Handlers ---
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Reset selected slot when tab changes
    const defaultSlot = newValue === 0 ? 'carousel_video1' : 'outro_video1';
    setSelectedSlotForSlider(defaultSlot);
    // Reset dynamic layout when tab changes to ensure correct base is used for pixel conversion
    setDynamicLayoutConfig(currentFormat === 'horizontal' ? HORIZONTAL_DEFAULT_LAYOUT : VERTICAL_DEFAULT_LAYOUT);
  };

  // --- TODO: Implement player click handler ---
  const handlePlayerClick = (slotId: string) => {
    if (dialogOpen) return; // Don't open dialog if already open
    console.log(`Player clicked: ${slotId}`);
    setSelectedSlot(slotId);
    setDialogOpen(true);
  };

  // --- TODO: Implement dialog close and save handlers ---
  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedSlot(null);
  };

  // Update handleSaveVideoSelection to use cached videos first when finding seek values
  const handleSaveVideoSelection = useCallback(async (
    slotId: string,
    newS3Url: string,
    seekValue: string // seekValue from dialog is now ignored for lookup, but kept for reference/future use
  ) => {
    if (!renderSheetId) return;

    console.log(`[IntroOutro] SAVING NEW VIDEO: Slot=${slotId}, URL=${newS3Url}, Original SeekValue from Dialog=${seekValue}`);

    try {
      // Determine if this is an intro or outro slot
      const isIntro = slotId.startsWith('carousel') || slotId === 'video_left';

      // Get cell addresses for the slot
      const cells = getCellsForSlot(slotId);
      if (!cells) {
        console.error(`[IntroOutro] Invalid slot ID: ${slotId}`);
        return;
      }

      // --- Simplified Seek Value Logic ---
      let finalSeekValue = '0'; // Default to '0'

      // Function to extract filename from URL for comparison
      const extractFilename = (url: string): string => {
        return url.split('/').pop()?.split('?')[0]?.toLowerCase() || '';
      };

      const urlFilename = extractFilename(newS3Url);
      console.log(`[IntroOutro] Looking for seek value for filename: ${urlFilename} ONLY in current week videos (render sheet data)`);

      // ONLY check currentWeekVideos
      const matchingVideo = currentWeekVideos.find(video => {
        const videoFilename = extractFilename(video.s3Url);
        const matches = videoFilename === urlFilename;
        // Optional console log for debugging matches
        // console.log(`[IntroOutro] Comparing with ${videoFilename} (${video.song}): ${matches ? 'MATCH' : 'no match'} (seek: ${video.seekIn})`);
        return matches;
      });

      if (matchingVideo && matchingVideo.seekIn && matchingVideo.seekIn !== '0') {
        finalSeekValue = matchingVideo.seekIn;
        console.log(`[IntroOutro] Found seek value in current week (render sheet) videos: ${finalSeekValue} for ${matchingVideo.song}`);
      } else {
        console.log(`[IntroOutro] Video or valid seek time (>0) not found in current week (render sheet) videos for ${urlFilename}. Defaulting seek to 0.`);
        // finalSeekValue remains '0'
      }
      // --- End Simplified Seek Value Logic ---


      console.log(`[IntroOutro] Using final seek value: ${finalSeekValue} for ${slotId}`);

      // Prepare batch update
      const updates = [
        { range: `Sheet1!${cells.urlCell}`, values: [[newS3Url]] }
      ];

      // Always update seek cell with the final seek value
      if (cells.seekCell) {
        updates.push({ range: `Sheet1!${cells.seekCell}`, values: [[finalSeekValue]] });
      }

      console.log(`[IntroOutro] Updating spreadsheet with batch updates:`, JSON.stringify(updates));

      // First update the spreadsheet
      await sheetsService.batchUpdateValues(renderSheetId, updates);

      // Then update the local state after spreadsheet update is successful
      const newData = {
        [`${slotId}_url`]: newS3Url,
        [`${slotId}_seek`]: finalSeekValue
      };

      if (isIntro) {
        setIntroSettings(prev => ({
          ...prev,
          ...newData
        }));
      } else {
        setOutroSettings(prev => ({
          ...prev,
          ...newData
        }));
      }

      // Close dialogs
      setDialogOpen(false);
      setSelectedSlot(null);

      // Also update selected slot for slider and slider value
      setSelectedSlotForSlider(slotId);
      const seekNumber = parseFloat(finalSeekValue);
      setSliderValue(isNaN(seekNumber) ? 0 : seekNumber);
      setTimeInputValue((isNaN(seekNumber) ? 0 : seekNumber).toFixed(1));

      console.log(`[IntroOutro] Successfully saved video selection for ${slotId}`);
    } catch (error) {
      console.error('[IntroOutro] Error saving video selection:', error);
    }
  }, [renderSheetId, currentWeekVideos]); // Removed masterVideoMap from dependencies

  // Modified handler for slot selection change
  const handleSlotSelectionChange = (event: SelectChangeEvent<string>) => {
    const selectedSlot = event.target.value as string;
    setSelectedSlotForSlider(selectedSlot);
    
    // Explicitly update slider value based on the selected slot
    let seekValue: string | undefined;
    
    if (selectedSlot.startsWith('carousel') || selectedSlot === 'video_left') {
      seekValue = (introSettings as any)[`${selectedSlot}_seek`];
    } else if (selectedSlot.startsWith('outro')) {
      seekValue = (outroSettings as any)[`${selectedSlot}_seek`];
    }
    
    // No need for special handling of video_left anymore since it now has a seek time
    // Parse and set the slider value
    const parsedValue = parseFloat(seekValue || '0');
    setSliderValue(isNaN(parsedValue) ? 0 : parsedValue);
    setTimeInputValue((isNaN(parsedValue) ? 0 : parsedValue).toFixed(1));
    
    console.log(`[IntroOutro] Selected slot ${selectedSlot} with seek value ${seekValue || '0'}`);
  };

  // Modified slider change handler to update preview state while dragging
  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setSliderValue(newValue as number);
    setIsDraggingSlider(true); // Set dragging flag when slider is being moved
  };

  // Modified slider change committed handler to restart playback after stopping
  const handleSliderChangeCommitted = useCallback(async (event: React.SyntheticEvent | Event, newValue: number | number[]) => {
    setIsDraggingSlider(false); // Reset dragging state when slider interaction ends
    
    if (!renderSheetId || !selectedSlotForSlider) return;

    const seekValueToSave = newValue as number;
    const cells = getCellsForSlot(selectedSlotForSlider);

    if (!cells || !cells.seekCell) {
      console.error(`Seek cell not found for slot: ${selectedSlotForSlider}`);
      setError(`Cannot update seek time for ${selectedSlotForSlider}.`);
      return;
    }

    const range = `Sheet1!${cells.seekCell}`;
    const updateData = [{ range: range, values: [[seekValueToSave.toString()]] }];

    console.log(`Updating seek time for ${selectedSlotForSlider} in cell ${range} to ${seekValueToSave}`);

    // ---> Add logic to update the main list cell
    try {
      // Find the video in the current week's data
      const currentUrl = selectedSlotForSlider.startsWith('outro')
        ? (outroSettings as any)[`${selectedSlotForSlider}_url`]
        : (introSettings as any)[`${selectedSlotForSlider}_url`];

      if (currentUrl && renderSheetSeekInColIndex !== -1) {
        // Find the video's *index* in the state array
        const videoIndex = currentWeekVideos.findIndex(v => v.s3Url === currentUrl);
        const videoToUpdate = videoIndex !== -1 ? currentWeekVideos[videoIndex] : null;
        
        // Check if the video was found using its index
        if (videoToUpdate && videoIndex !== -1) { 
          // Convert column index to letter (A=0, B=1, ..., Z=25)
          const getColumnLetter = (index: number): string => {
            let letter = '';
            let tempIndex = index;
            while (tempIndex >= 0) {
              letter = String.fromCharCode((tempIndex % 26) + 65) + letter;
              tempIndex = Math.floor(tempIndex / 26) - 1;
            }
            return letter;
          };
          
          const seekInColumnLetter = getColumnLetter(renderSheetSeekInColIndex);
          // Calculate row number based on array index (+2 because array index 0 = row 2)
          const targetRowNum = videoIndex + 2; 
          const mainListUpdateRange = `Sheet1!${seekInColumnLetter}${targetRowNum}`;
          
          if (mainListUpdateRange) {
            console.log(`Found video "${videoToUpdate.song}" at index ${videoIndex}. Also updating main list seek time in cell ${mainListUpdateRange}`);
            updateData.push({
              range: mainListUpdateRange,
              values: [[seekValueToSave.toString()]]
            });
          } else {
            // Update warning message
            console.warn(`[IntroOutro] Could not find video with URL ${currentUrl} in currentWeekVideos to update main list.`);
          }
        } else {
          // Update warning message
          console.warn(`[IntroOutro] Could not find video with URL ${currentUrl} in currentWeekVideos to update main list.`);
        }
      } else {
        console.warn(`[IntroOutro] Skipping main list update: URL missing or Seek In column index invalid (${renderSheetSeekInColIndex}).`);
      }
    } catch (findError) {
      console.error("[IntroOutro] Error finding video data for main list update:", findError);
    }
    // ---> END: Add logic to update the main list cell

    setIsUpdatingSheet(true);
    setError(null);

    // ---> START: Log the exact data being sent
    console.log(`[IntroOutro] Sending batch update to Sheets:`, JSON.stringify(updateData, null, 2));
    // ---> END: Log the exact data being sent

    try {
      // Use batchUpdateValues for consistency, now potentially with multiple updates
      await sheetsService.batchUpdateValues(renderSheetId, updateData);

      // Update local state as well
      if (selectedSlotForSlider.startsWith('carousel') || selectedSlotForSlider === 'video_left') {
        setIntroSettings(prev => ({ ...prev, [`${selectedSlotForSlider}_seek`]: seekValueToSave.toString() }));
      } else if (selectedSlotForSlider.startsWith('outro')) {
        setOutroSettings(prev => ({ ...prev, [`${selectedSlotForSlider}_seek`]: seekValueToSave.toString() }));
      }
      console.log('Seek time updated successfully.');

    } catch (err) {
      console.error('Error updating seek time:', err);
      setError(err instanceof Error ? err.message : 'Failed to update seek time.');
    } finally {
      setIsUpdatingSheet(false);
    }
  }, [renderSheetId, selectedSlotForSlider, introSettings, outroSettings, currentWeekVideos, renderSheetSeekInColIndex, setError, setIsUpdatingSheet, setIntroSettings, setOutroSettings]);

  // Add handler for direct time input change
  const handleTimeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTimeInputValue(e.target.value);
  };

  // Add handler for when time input is submitted
  const handleTimeInputBlur = () => {
    // Parse the input value and validate
    const parsedValue = parseFloat(timeInputValue);
    if (isNaN(parsedValue) || parsedValue < 0 || parsedValue > 300) {
      // Reset to current slider value if invalid or out of range
      setTimeInputValue(sliderValue.toFixed(1));
      return;
    }
    
    // Update slider value
    setSliderValue(parsedValue);
    
    // If not currently updating, save the value
    if (!isUpdatingSheet) {
      handleSliderChangeCommitted({} as React.SyntheticEvent, parsedValue);
    }
  };

  // Add handler for key press in time input
  const handleTimeInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur(); // Trigger the blur event to save
    }
  };

  // --- Draggable and Resizable Handlers --- (Temporarily disabled)
  // const handleDragStop = (slotId: string, e: DraggableEvent, data: DraggableData) => {
  //   setDynamicLayoutConfig(prevConfig => ({
  //     ...prevConfig,
  //     [slotId]: {
  //       ...prevConfig[slotId],
  //       top: `${data.y}px`,
  //       left: `${data.x}px`,
  //     }
  //   }));
  // };

  // const handleResizeStop = (slotId: string, e: React.SyntheticEvent, data: { size: { width: number; height: number } }) => {
  //   setDynamicLayoutConfig(prevConfig => ({
  //     ...prevConfig,
  //     [slotId]: {
  //       ...prevConfig[slotId],
  //       width: `${data.size.width}px`,
  //       height: `${data.size.height}px`,
  //     }
  //   }));
  // };

  // Add an effect to debug the background image issues
  useEffect(() => {
    if (activeTab === 0) {
      console.log(`Showing intro background image: ${process.env.PUBLIC_URL}/intro.jpg`);
    } else {
      console.log(`Showing outro background image: ${process.env.PUBLIC_URL}/outro.jpg`);
    }
  }, [activeTab]);

  // Save Seek Times function - Reads from Render Sheet Col P, Writes to Master Sheet Col E
  const saveMasterSeekTimes = useCallback(async () => {
    console.log('[IntroOutro DEBUG] STARTING saveMasterSeekTimes function (Render Sheet -> Master Sheet Sync)...');
    
    if (!renderSheetId) { 
      console.error('[IntroOutro ERROR] Render Sheet ID is missing.'); 
      setError('Render Sheet ID missing.'); 
      return; 
    }
    
    // Use hard-coded constant instead of environment variable
    const masterSpreadsheetId = MASTER_VIDEO_LIST_ID;
    console.log(`[IntroOutro DEBUG] Using Master Sheet ID: ${masterSpreadsheetId}`);
    
    if (!masterSpreadsheetId) { 
      console.error('[IntroOutro ERROR] Master Sheet ID is missing or undefined.'); 
      setError('Configuration error: Master Sheet ID missing.'); 
      return; 
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Fetch the 10 songs from Render Sheet (rows 2-11)
      console.log('[IntroOutro DEBUG] Fetching current week songs from Render Sheet...');
      const renderSheetData = await sheetsService.getData(renderSheetId, 'Sheet1!A2:P11');
      
      if (!renderSheetData?.values) {
        console.error('[IntroOutro ERROR] No song data found in Render Sheet.');
        setError('No song data found in Render Sheet.');
        setLoading(false);
        return;
      }
      
      // 2. Extract S3 URLs and seek values - UPDATED INDICES
      // First, detect columns positions from a sample row
      const firstRow = renderSheetData.values[0] || [];
      // Look for S3 URL column - usually column C (index 2)
      let s3UrlColIndex = -1;
      for (let i = 0; i < firstRow.length; i++) {
        const value = String(firstRow[i] || '').toLowerCase();
        if (value.includes('s3.amazonaws.com') || value.includes('c1-scott')) {
          s3UrlColIndex = i;
          break;
        }
      }
      
      // If we couldn't find it by content, use the default index
      if (s3UrlColIndex === -1) {
        s3UrlColIndex = 12; // Column M (0-indexed) - try this as a fallback
        console.log(`[IntroOutro DEBUG] Couldn't detect S3 URL column, falling back to index ${s3UrlColIndex} (column ${String.fromCharCode(65 + s3UrlColIndex)})`);
      } else {
        console.log(`[IntroOutro DEBUG] Detected S3 URL column at index ${s3UrlColIndex} (column ${String.fromCharCode(65 + s3UrlColIndex)})`);
      }
      
      const seekColIndex = 15;  // Column P (0-indexed)
      
      // Create array of updates to make to Master Sheet
      const songsToUpdate: { s3Url: string; seekTime: string; song?: string; artist?: string }[] = [];
      
      renderSheetData.values.forEach((row, index) => {
        if (s3UrlColIndex >= row.length) {
          console.log(`[IntroOutro DEBUG] Row ${index} doesn't have enough columns (${row.length}), skipping`);
          return;
        }
        
        const s3Url = row[s3UrlColIndex]?.toString().trim();
        const seekTime = row[seekColIndex]?.toString().trim() || '0';
        const song = row[0]?.toString().trim(); // Song name from column A
        const artist = row[1]?.toString().trim(); // Artist name from column B
        
        if (!s3Url) {
          console.log(`[IntroOutro DEBUG] No S3 URL found in row ${index+2}, song: "${song}"`);
          return;
        }
        
        if (!s3Url.includes('s3.amazonaws.com') && !s3Url.includes('c1-scott')) {
          console.log(`[IntroOutro DEBUG] Value in S3 URL column doesn't look like a URL: "${s3Url}" for song "${song}"`);
          return;
        }
        
        songsToUpdate.push({ s3Url, seekTime, song, artist });
        console.log(`[IntroOutro DEBUG] Song to update: "${song}" by ${artist}, URL=${s3Url}, Seek=${seekTime}`);
        
        // Special debug for Pink Pony Club
        if (song?.includes("Pink Pony Club") || s3Url.includes("Pink Pony Club")) {
          console.log(`[IntroOutro DEBUG] FOUND PINK PONY CLUB in render sheet. Seek time = ${seekTime}`);
        }
      });
      
      if (songsToUpdate.length === 0) {
        console.warn('[IntroOutro WARN] No songs with S3 URLs found in Render Sheet.');
        setError('No songs with S3 URLs found in Render Sheet.');
        setLoading(false);
        return;
      }
      
      // 3. Get Master Sheet data
      console.log(`[IntroOutro DEBUG] Fetching Master Sheet data (${masterSpreadsheetId})...`);
      const masterSheetData = await sheetsService.getData(masterSpreadsheetId, 'Sheet1!A:Z');
      
      if (!masterSheetData?.values) {
        console.error('[IntroOutro ERROR] No data found in Master Sheet.');
        setError('Failed to retrieve Master Sheet data.');
        setLoading(false);
        return;
      }
      
      // Debug master sheet data
      console.log(`[IntroOutro DEBUG] Master Sheet data contains ${masterSheetData.values.length} rows (including header)`);
      console.log(`[IntroOutro DEBUG] Master Sheet header row: ${JSON.stringify(masterSheetData.values[0])}`);
      console.log(`[IntroOutro DEBUG] Sample data (first 3 rows after header):`);
      for (let i = 1; i < Math.min(masterSheetData.values.length, 4); i++) {
        console.log(`[IntroOutro DEBUG] Row ${i}: ${JSON.stringify(masterSheetData.values[i])}`);
      }
      
      // 4. Find column indices in Master Sheet (look for "Seek In" by header name)
      const masterHeaders = masterSheetData.values[0].map((h: string) => String(h || '').toLowerCase());
      const masterS3UrlColIndex = masterHeaders.findIndex(h => h.includes('s3') || h.includes('url') || h.includes('link'));
      
      // Look specifically for a column called "Seek In" (case insensitive)
      const seekInColIndex = masterHeaders.findIndex(h => h.toLowerCase() === 'seek in');
      
      // If not found, try less strict matching
      let targetColIndex = seekInColIndex;
      if (targetColIndex === -1) {
        // Try less strict matching - any column that contains "seek"
        targetColIndex = masterHeaders.findIndex(h => h.toLowerCase().includes('seek'));
      }
      
      // If still not found, try other likely columns
      if (targetColIndex === -1) {
        // Look for columns with "time" or "point"
        targetColIndex = masterHeaders.findIndex(h => 
          h.toLowerCase().includes('time') || 
          h.toLowerCase().includes('point')
        );
      }
      
      // As last resort, use column E (index 4) for backward compatibility
      if (targetColIndex === -1) {
        targetColIndex = 4; // Default to column E
        console.log(`[IntroOutro DEBUG] Could not find "Seek In" column in headers: ${JSON.stringify(masterHeaders)}`);
        console.log(`[IntroOutro DEBUG] Falling back to default column E (index 4)`);
      }
      
      // Convert column index to letter (A=0, B=1, etc.)
      const targetColumnLetter = String.fromCharCode(65 + targetColIndex);
      
      console.log(`[IntroOutro DEBUG] Master Sheet column analysis:`);
      console.log(`- S3 URL: column index ${masterS3UrlColIndex} (${String.fromCharCode(65 + masterS3UrlColIndex)})`);
      console.log(`- Seek In: column index ${targetColIndex} (${targetColumnLetter})`);
      
      if (masterS3UrlColIndex === -1) {
        console.error('[IntroOutro ERROR] Could not find S3 URL column in Master Sheet.');
        setError('Failed to locate S3 URL column in Master Sheet.');
        setLoading(false);
        return;
      }
      
      // 5. Prepare updates for Master Sheet
      const updates: { range: string; values: any[][] }[] = [];
      const matchedSongs: string[] = [];
      
      // For each song to update, find it in Master Sheet and prepare the update
      songsToUpdate.forEach(songToUpdate => {
        const { s3Url, seekTime, song } = songToUpdate;
        let foundMatch = false;
        
        // Get just the filename part of the URL
        const renderFilename = s3Url.split('/').pop()?.split('?')[0]?.toLowerCase();
        
        // Find the row in Master Sheet with matching S3 URL
        for (let i = 1; i < masterSheetData.values.length; i++) {
          const masterRow = masterSheetData.values[i];
          if (!masterRow[masterS3UrlColIndex]) continue;
          
          const masterS3Url = masterRow[masterS3UrlColIndex].toString().trim();
          const currentMasterSeek = masterRow[targetColIndex]?.toString().trim() || '0';
          
          // Check both full URL match and filename match
          const masterFilename = masterS3Url.split('/').pop()?.split('?')[0]?.toLowerCase();
          
          if ((masterS3Url === s3Url) || (renderFilename && masterFilename && renderFilename === masterFilename)) {
            // Found a match - prepare update using the target column letter
            const rowNumber = i + 1; // Convert to 1-indexed
            updates.push({
              range: `Sheet1!${targetColumnLetter}${rowNumber}`,
              values: [[seekTime]]
            });
            
            matchedSongs.push(song || 'Unknown');
            
            // Special debug for Pink Pony Club
            if (song?.includes("Pink Pony Club") || s3Url.includes("Pink Pony Club") || masterS3Url.includes("Pink Pony Club")) {
              console.log(`[IntroOutro DEBUG] PINK PONY CLUB found in master sheet at row ${rowNumber}`);
              console.log(`[IntroOutro DEBUG] Current seek in master (column ${targetColumnLetter}): ${currentMasterSeek}, New seek from render: ${seekTime}`);
              console.log(`[IntroOutro DEBUG] Will update cell ${targetColumnLetter}${rowNumber} with seek time ${seekTime}`);
              console.log(`[IntroOutro DEBUG] Master URL: ${masterS3Url}`);
              console.log(`[IntroOutro DEBUG] Render URL: ${s3Url}`);
            }
            
            console.log(`[IntroOutro DEBUG] Found match in Master Sheet row ${rowNumber}, updating seek time from ${currentMasterSeek} to ${seekTime} in column ${targetColumnLetter}`);
            foundMatch = true;
            break;
          }
        }
        
        if (!foundMatch && (song?.includes("Pink Pony Club") || s3Url.includes("Pink Pony Club"))) {
          console.log(`[IntroOutro DEBUG] PINK PONY CLUB NOT FOUND in master sheet! URL = ${s3Url}`);
        }
      });
      
      // 5. Apply the updates
      if (updates.length > 0) {
        console.log(`[IntroOutro DEBUG] Sending ${updates.length} updates to Master Sheet...`);
        console.log(`[IntroOutro DEBUG] Update details:`, JSON.stringify(updates));
        await sheetsService.batchUpdateValues(masterSpreadsheetId, updates);
        console.log('[IntroOutro DEBUG] ✅ Successfully updated seek times in Master Sheet.');
      } else {
        console.warn('[IntroOutro WARN] No matching songs found in Master Sheet.');
        setError('No matching songs found in Master Sheet.');
      }

    } catch (error) {
      console.error('[IntroOutro ERROR] Error updating Master Sheet:', error);
      setError(error instanceof Error ? error.message : 'Failed to update seek times in Master Sheet.');
    } finally {
      setLoading(false);
    }
  }, [renderSheetId, setError, setLoading, sheetsService]);

  // Handle navigation with save
  const handleReturnToCharts = async () => {
    console.log('[IntroOutro DEBUG] Return to Charts button clicked, saving seek times before navigation...');
    setLoading(true);
    try {
      await saveMasterSeekTimes();
      console.log('[IntroOutro DEBUG] Successfully executed saveMasterSeekTimes, navigating back to charts...');
      navigate('/');
    } catch (err) {
      console.error('[IntroOutro DEBUG] Error during navigation:', err);
      console.error('[IntroOutro DEBUG] Navigating anyway despite error');
      navigate('/'); // Navigate even if there was an error
    } finally {
      setLoading(false);
    }
  };

  // Modify the delayed cleanup useEffect to ensure it runs for all dates, even with no videos
  useEffect(() => {
    if (!loading) {
      // Create a hash of current videos to track if we've already cleaned up this set
      // If there are no videos, use a special key
      const videosHash = currentWeekVideos.length > 0 
        ? currentWeekVideos.map(v => v.s3Url).sort().join('|')
        : 'NO_VIDEOS';
      
      console.log(`[IntroOutro] Current videos hash: ${videosHash.substring(0, 40)}...`);
      console.log(`[IntroOutro] Already cleaned up? ${Boolean(cleanupRunRef.current[videosHash])}`);
      
      // Only run cleanup if we haven't already done it for this set of videos
      if (!cleanupRunRef.current[videosHash]) {
        // Add a delay to ensure videos are rendered before cleanup runs
        const cleanupTimer = setTimeout(() => {
          console.log('[IntroOutro] Running cleanup check after videos have rendered...');
          cleanupOutdatedVideos(currentWeekVideos);
          
          // Mark this set of videos as processed
          cleanupRunRef.current[videosHash] = true;
        }, 2000); // 2 second delay to ensure videos have loaded
        
        return () => clearTimeout(cleanupTimer);
      }
    }
  }, [currentWeekVideos, loading, cleanupOutdatedVideos]);

  // --- Render Logic ---
  if (!renderSheetId) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Render Sheet ID was not provided. Please navigate from the main charts page.</Alert>
        <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
          Go to Charts
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 4 }}>{error}</Alert>;
  }

  // Helper to render players for a given section
  const renderPlayers = (settings: IntroSettings | OutroSettings | undefined, prefix: string, count: number, isLeft?: boolean) => {
    if (!settings) return []; // Add a guard for undefined settings

    const players = [];
    // Use dynamicLayoutConfig instead of static defaults
    // const activeLayout = currentFormat === 'horizontal' ? HORIZONTAL_DEFAULT_LAYOUT : VERTICAL_DEFAULT_LAYOUT;

    for (let i = 1; i <= count; i++) {
      const slotId = isLeft ? prefix : `${prefix}_video${i}`;
      
      if (currentFormat === 'vertical' && slotId === 'outro_video2') {
        continue;
      }

      const url = (settings as any)[`${slotId}_url`];
      const seek = (settings as any)[`${slotId}_seek`];
      
      const layout = dynamicLayoutConfig[slotId];
      const safeLayout = layout || { top: '0%', left: '0%', width: '150px', height: '84px'}; 

      // Debug logging for outro_video6
      if (slotId === 'outro_video6') {
        console.log(`[DEBUG] outro_video6 layout:`, layout);
        console.log(`[DEBUG] outro_video6 safeLayout:`, safeLayout);
        console.log(`[DEBUG] currentFormat:`, currentFormat);
        console.log(`[DEBUG] activeTab:`, activeTab);
      }

      const isPreviewSlot = isDraggingSlider && slotId === selectedSlotForSlider;
      let songInfo = null;
      if (url) {
        const trimmedUrl = url.trim(); // Ensure URL is trimmed
        console.log(`[IntroOutro] Looking up info for ${slotId}, URL: ${trimmedUrl}`);
        
        // Helper function to extract and normalize filename
        const extractFilename = (fileUrl: string): string => {
          const filename = fileUrl.split('/').pop() || '';
          // Remove any query parameters
          return filename.split('?')[0].toLowerCase();
        };
        
        // Try to find exact URL match (with trimming)
        songInfo = Array.from(masterVideoMap.values()).find(info => info.s3Url.trim() === trimmedUrl);
        
        if (songInfo) {
          console.log(`[IntroOutro] Found exact URL match for ${slotId}: ${songInfo.song} - ${songInfo.artist}`);
        }
        
        // If not found, try to match filename
        if (!songInfo) {
          const urlFilename = extractFilename(trimmedUrl);
          console.log(`[IntroOutro] Trying filename match for ${slotId}, filename: ${urlFilename}`);
          
          songInfo = Array.from(masterVideoMap.values()).find(info => {
            const infoFilename = extractFilename(info.s3Url);
            const matches = urlFilename === infoFilename;
            if (matches) {
              console.log(`[IntroOutro] Found filename match for ${slotId}: ${info.song} - ${info.artist}`);
            }
            return matches;
          });
        }
        
        // If still not found, check current week videos
        if (!songInfo && currentWeekVideos.length > 0) {
          // Try exact URL match first
          songInfo = currentWeekVideos.find(info => info.s3Url.trim() === trimmedUrl);
          
          if (songInfo) {
            console.log(`[IntroOutro] Found exact URL match in current week videos for ${slotId}: ${songInfo.song} - ${songInfo.artist}`);
          }
          
          // Try filename match with current week videos
          if (!songInfo) {
            const urlFilename = extractFilename(trimmedUrl);
            songInfo = currentWeekVideos.find(info => {
              const infoFilename = extractFilename(info.s3Url);
              const matches = urlFilename === infoFilename;
              if (matches) {
                console.log(`[IntroOutro] Found filename match in current week videos for ${slotId}: ${info.song} - ${info.artist}`);
              }
              return matches;
            });
          }
        }
        
        // If still not found, try partial filename match as last resort
        if (!songInfo) {
          const urlFilename = extractFilename(trimmedUrl);
          console.log(`[IntroOutro] Trying partial filename match for ${slotId}, filename: ${urlFilename}`);
          
          // Try all videos from master map first
          const allVideos = [...Array.from(masterVideoMap.values()), ...currentWeekVideos];
          
          songInfo = allVideos.find(info => {
            const infoFilename = extractFilename(info.s3Url);
            
            // Check if filenames share substantial part (at least 60% match)
            const longerName = urlFilename.length > infoFilename.length ? urlFilename : infoFilename;
            const shorterName = urlFilename.length <= infoFilename.length ? urlFilename : infoFilename;
            
            if (shorterName.length > 5 && longerName.includes(shorterName)) {
              console.log(`[IntroOutro] Found partial filename match for ${slotId}: ${info.song} - ${info.artist}`);
              return true;
            }
            
            return false;
          });
        }
        
        if (!songInfo) {
          console.log(`[IntroOutro] No song/artist info found for ${slotId}`);
        }
      }

      const playerElement = (
        <Box sx={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}> {/* Added overflow hidden */}
          <MiniVideoPlayer 
            key={`${slotId}-player`} 
            slotId={slotId}
            s3Url={url}
            seekValue={seek}
            previewTime={isPreviewSlot ? sliderValue : undefined} // Pass preview time only to selected slot when dragging
            onClick={handlePlayerClick} 
            style={{ width: '100%', height: '100%' }} // Player fills the container
            song={songInfo?.song}
            artist={songInfo?.artist}
          />
          {/* Place label inside the video at the top */}
          <Typography 
            variant="caption" 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              textAlign: 'center', 
              fontSize: '8px', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: 'white',
              padding: '1px'
            }}
          >
            {slotId}
          </Typography>
        </Box>
      );

      // Temporarily disabled draggable/resizable functionality due to React 18 compatibility
      // Just render static positioned videos for now
      players.push(
        <Box 
          key={slotId} 
          sx={{ ...safeLayout, position: 'absolute' }}
        >
          {playerElement}
        </Box>
      );
    }
    return players;
  };

  // Dynamically generate slot IDs for the dropdown based on the active tab, with video_left first if applicable
  const currentSlotIds = activeTab === 0 
    ? ['video_left', 'carousel_video1', 'carousel_video2', 'carousel_video3', 'carousel_video4']
    : (currentFormat === 'horizontal' 
        ? ['outro_video1', 'outro_video2', 'outro_video3', 'outro_video4', 'outro_video5', 'outro_video6']
        : ['outro_video1', 'outro_video3', 'outro_video4', 'outro_video5', 'outro_video6']); // Vertical outro omits outro_video2

  // Check if the slider should be disabled - updated to not disable for video_left
  const isSliderDisabled = isUpdatingSheet;

  // Add Output Manager handler function
  const handleOutputManager = async () => {
    // Get the render sheet ID from the URL path
    const pathParts = location.pathname.split('/');
    const renderSheetId = pathParts[pathParts.length - 1];
    
    if (!renderSheetId) {
      enqueueSnackbar('No render sheet ID found', { variant: 'error' });
      return;
    }
    
    localStorage.setItem('currentSpreadsheetId', renderSheetId);
    // Navigate with parentSpreadsheetId as query parameter
    navigate(`/output-manager?parentSpreadsheetId=${renderSheetId}`);
  };

  return (
    <Box sx={{ p: 4 }}> {/* Outermost container for padding */}
      <Box sx={{ // Intermediate container for width control and centering
        width: currentFormat === 'vertical' ? '50%' : '100%',
        margin: currentFormat === 'vertical' ? '0 auto' : '0',
        // display: 'flex', // Removed flex properties
        // flexDirection: 'column', // Removed flex properties
      }}>
        {/* All content from here down is inside the new intermediate Box */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 1, 
          width: '100%' // Takes full width of the new intermediate parent
        }}>
          <Button variant="contained" onClick={handleReturnToCharts} sx={{ mr: 2 }}>
            Charts
          </Button>
          <Button
            variant="contained"
            onClick={handleOutputManager}
            size="small"
            sx={{
              height: '40px',
              padding: '4px 16px',
              display: 'flex',
              alignItems: 'center',
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#00FF9A',
              color: '#000000',
              '&:hover': {
                backgroundColor: '#00e68a',
                color: '#000000',
              },
            }}
          >
            OUTPUT MANAGER
          </Button>
        </Box>

        {/* Display cleanup notification if videos were removed */}
        {cleanupNotification && (
          <Alert severity="info" sx={{ mb: 2, width: '100%' }} onClose={() => setCleanupNotification(null)}>
            <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
              {cleanupNotification}
            </Typography>
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, width: '100%' }}> {/* Ensure this Box also takes full width */}
          <ToggleButtonGroup
            color="primary"
            value={currentFormat}
            exclusive
            onChange={handleFormatChange}
            aria-label="Video Format"
          >
            <ToggleButton value="horizontal">Horizontal</ToggleButton>
            <ToggleButton value="vertical">Vertical</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Tabs value={activeTab} onChange={handleTabChange} centered sx={{ mb: 3, width: '100%' }}>
          <Tab label="Intro" />
          <Tab label="Outro" />
        </Tabs>

        {/* Background and Player Container */}
        <Box 
          sx={{
            position: 'relative',
            width: '100%', // Takes full width of its new intermediate parent
            paddingTop: currentFormat === 'horizontal' ? '56.25%' : '177.78%', // Dynamic aspect ratio
            backgroundColor: 'rgba(0,0,0,0.1)',
            overflow: 'hidden',
            border: '1px solid #ccc',
          }}
        >
          {/* Background image in its own layer */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${activeTab === 0 ? (currentFormat === 'horizontal' ? '/intro.jpg' : '/v-intro.jpg') : (currentFormat === 'horizontal' ? '/outro.jpg' : '/v-outro.jpg')})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              zIndex: 0
            }}
          />
          
          {/* Content layer */}
          <Box
            ref={playerAreaRef} // Assign ref here
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1
            }}
          >
            {/* Render Players based on active tab using DEFAULT_LAYOUT */}
            {activeTab === 0 && renderPlayers(introSettings, 'carousel', 4)}
            {activeTab === 0 && renderPlayers(introSettings, 'video_left', 1, true)}
            {activeTab === 1 && renderPlayers(outroSettings, 'outro', 6)} 
          </Box>
        </Box>

        {/* Seek Time Adjustment Controls */}
        <Box sx={{ 
          mt: 3, 
          p: 2, 
          border: '1px solid #ddd', 
          borderRadius: '4px', 
          width: '100%' // Takes full width of its new intermediate parent
        }}>
          <Typography variant="h6" gutterBottom>Adjust Seek In Time</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flexBasis: { xs: '100%', sm: '30%' } }}>
              <FormControl fullWidth size="small">
                <InputLabel id="select-slot-label">Select Video Slot</InputLabel>
                <Select
                  labelId="select-slot-label"
                  id="select-slot"
                  value={selectedSlotForSlider}
                  label="Select Video Slot"
                  onChange={handleSlotSelectionChange}
                >
                  {currentSlotIds.map(slotId => (
                    <MenuItem key={slotId} value={slotId}>{slotId.replace(/_/g, ' ').replace(/video/g, 'Video')}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flexBasis: { xs: '100%', sm: '65%' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Slider
                  value={sliderValue}
                  onChange={handleSliderChange}
                  onChangeCommitted={handleSliderChangeCommitted}
                  aria-labelledby="seek-slider"
                  valueLabelDisplay="auto"
                  step={0.1}
                  min={0}
                  max={300}
                  disabled={isSliderDisabled}
                  sx={{ mr: 2 }}
                />
                {isSliderDisabled ? (
                  <Typography variant="body2" sx={{ minWidth: '40px', textAlign: 'right' }}>
                    N/A
                  </Typography>
                ) : (
                  <TextField
                    value={timeInputValue}
                    onChange={handleTimeInputChange}
                    onBlur={handleTimeInputBlur}
                    onKeyDown={handleTimeInputKeyDown}
                    disabled={isUpdatingSheet}
                    size="small"
                    sx={{ 
                      width: '120px',
                      '& input': { 
                        textAlign: 'right',
                        padding: '4px 8px'
                      }
                    }}
                    InputProps={{
                      endAdornment: <Typography variant="body2" sx={{ ml: 0.5 }}>s</Typography>
                    }}
                  />
                )}
                {isUpdatingSheet && <CircularProgress size={20} sx={{ ml: 1 }} />} 
              </Box>
            </Box>
          </Box>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>} 
        </Box>

        {/* Video Selection Dialog */}
        {dialogOpen && (
          <VideoSelectionDialog 
            open={dialogOpen}
            onClose={handleDialogClose}
            masterVideoList={currentWeekVideos.length > 0 ? currentWeekVideos : Array.from(masterVideoMap.values())} 
            currentSlotId={selectedSlot}
            onSave={handleSaveVideoSelection}
            activeTab={activeTab}
            introSettings={introSettings as any} 
            outroSettings={outroSettings as any} 
          /> 
        )}
      </Box> {/* THIS IS THE CORRECTED CLOSING TAG for the new intermediate Box */}
    </Box>
  );
};

export default IntroOutroPage;